import React, { useState, useRef, useEffect } from 'react';
import { useSpeech } from '../hooks/useSpeech';
import './TypingPractice.css';

export const TypingPractice = ({
  card,
  direction,
  onAnswer,
  onNext,
  counter,
  score,
  autoAdvance = true
}) => {
  const [userAnswer, setUserAnswer] = useState('');
  const [isChecked, setIsChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const inputRef = useRef(null);
  const { isSupported, isSpeaking, speak } = useSpeech();

  useEffect(() => {
    // Reset state when card changes
    setUserAnswer('');
    setIsChecked(false);
    setIsCorrect(null);
    setShowHint(false);

    // Focus input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [card]);

  if (!card) {
    return (
      <div className="tp-card">
        <div className="tp-message">No cards available</div>
      </div>
    );
  }

  const question = direction === 'ar' ? card.ar : card.en;
  const correctAnswer = direction === 'ar' ? card.en : card.ar;

  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, ''); // Remove punctuation
  };

  const checkAnswer = () => {
    if (isChecked) return; // Already checked

    const normalized = normalizeText(userAnswer);
    const normalizedCorrect = normalizeText(correctAnswer);

    const correct = normalized === normalizedCorrect;
    setIsCorrect(correct);
    setIsChecked(true);

    // Notify parent
    onAnswer(correct);

    // Auto-advance if enabled
    if (autoAdvance && onNext) {
      setTimeout(() => {
        onNext();
      }, 1500); // 1.5 second delay to see feedback
    }
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (!isChecked && userAnswer.trim()) {
        checkAnswer();
      } else if (isChecked && !autoAdvance && onNext) {
        // Only allow manual advance with Enter if auto-advance is disabled
        handleNext();
      }
    }
  };

  const getHint = () => {
    const hint = correctAnswer.split('').map((char, idx) => {
      if (idx < Math.floor(correctAnswer.length / 3)) {
        return char;
      } else if (char === ' ') {
        return ' ';
      } else {
        return '_';
      }
    }).join('');
    return hint;
  };

  return (
    <div className="tp-card">
      <div className="tp-header">
        <div className="tp-counter">{counter}</div>
        <div className="tp-score">Score: {score.correct}/{score.total}</div>
      </div>

      <div className="tp-question-section">
        <div className="tp-question-header">
          <div className="tp-question-label">
            {direction === 'ar' ? 'Type the English translation:' : 'اكتب الترجمة العربية:'}
          </div>
          {card.dialect && <div className="dialect-badge-small">Dialect</div>}
        </div>
        <div className="tp-question" dir={direction === 'ar' ? 'rtl' : 'ltr'}>
          {question}
        </div>
        {isSupported && (
          <button
            className={`speak-btn ${isSpeaking ? 'speaking' : ''}`}
            onClick={() => speak(question, direction === 'ar' ? 'ar-SA' : 'en-US')}
            title="Listen to pronunciation"
          >
            🔊
          </button>
        )}
      </div>

      <div className="tp-input-section">
        <input
          ref={inputRef}
          type="text"
          className={`tp-input ${isChecked ? (isCorrect ? 'tp-correct' : 'tp-incorrect') : ''}`}
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your answer..."
          disabled={isChecked}
          dir={direction === 'ar' ? 'ltr' : 'rtl'}
          autoFocus
        />

        {!isChecked && (
          <button
            className="tp-submit-btn"
            onClick={checkAnswer}
            disabled={!userAnswer.trim()}
          >
            Check Answer
          </button>
        )}
      </div>

      {!isChecked && (
        <div className="tp-hint-section">
          {!showHint ? (
            <button className="tp-hint-btn" onClick={() => setShowHint(true)}>
              💡 Show Hint
            </button>
          ) : (
            <div className="tp-hint">
              Hint: {getHint()}
            </div>
          )}
        </div>
      )}

      {isChecked && (
        <>
          <div className={`tp-result ${isCorrect ? 'tp-result-correct' : 'tp-result-incorrect'}`}>
            <div className="tp-result-icon">
              {isCorrect ? '✓' : '✗'}
            </div>
            <div className="tp-result-text">
              {isCorrect ? 'Correct!' : 'Incorrect'}
            </div>
            {!isCorrect && (
              <div className="tp-correct-answer">
                Correct answer: <strong>{correctAnswer}</strong>
              </div>
            )}
          </div>
          {!autoAdvance && (
            <button className="tp-next-btn" onClick={handleNext}>
              Next →
            </button>
          )}
        </>
      )}

      <div className="tp-instructions">
        {isChecked ? (
          !autoAdvance ? (
            <>Press <kbd>Enter</kbd> to continue</>
          ) : (
            <>Moving to next question...</>
          )
        ) : (
          <>Press <kbd>Enter</kbd> to submit your answer</>
        )}
      </div>
    </div>
  );
};
